import { useEffect, useRef, useState } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  matchPath,
  useLocation,
} from 'react-router-dom';
import { useMedia } from 'react-use';
import { useLogUser } from '@hungryroot/customer';
import {
  getApplicablePromo,
  getCyoPromo,
  getPromos,
  OrderSummary,
  OrderSummaryFooterOverlay,
  OrderSummaryTotalAmount as OrderSummaryTotalAmountVariant,
  Header,
} from '@hungryroot/exp-159-precheckout-plan-redesign';
import { useExperiment, useLogExperiments } from '@hungryroot/experiments';
import { FlexCreditCheckoutMessage } from '@hungryroot/flex-credits';
import { useGoogleAnalytics } from '@hungryroot/google-analytics';
import { OpenBoxNew } from '@hungryroot/icons';
import { OneColTwoCol } from '@hungryroot/layout';
import { Loading } from '@hungryroot/loading-component-primitives';
import { init as initO11y, log } from '@hungryroot/o11y';
import {
  useGetAddress,
  usePatchCustomerAddress,
  ErrorMessage,
  getAddressRequestBody,
  getDisplayAddress,
  getDisplayAddressDetails,
  validateRequiredFields,
  getAddressErrorMessage,
  type Address,
  type AddressDetails,
  type ErrorFields,
} from '@hungryroot/pages-checkout';
import {
  useCustomerCheckoutPricingInfo,
  getPromoForOverlay,
} from '@hungryroot/plan';
import {
  SODIUM_HARD_CONSTRAINT_OPT_OUT_LS_KEY,
  useClearQuizLocalStorage,
} from '@hungryroot/quiz';
import {
  Heading,
  Paragraph,
  Button,
  type HeadingVariant,
  type SubmitButtonProps,
} from '@hungryroot/ramen';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import {
  useGetPossibleDays,
  type DateInfo,
} from '../../api/get_possible_days/get/get_possible_days';
import { usePostSaveShippingDay } from '../../api/save_shipping_day/save_shipping_day';
import {
  AuthGuard,
  CustomerContext,
} from '../../components/auth/AuthGuard/AuthGuard';
import { CalloutBox } from '../../components/CalloutBox/CalloutBox';
import { LOGO_PATH } from '../../components/navigation/desktop/DesktopNav';
import { getNumPromosWithDollarEffect } from '../../components/OrderSummary/OrderSummary';
import { OrderSummaryTotalAmount } from '../../components/OrderSummaryTotalAmount/OrderSummaryTotalAmount';
import { useAuthState } from '../../customer/use-customer';
import {
  PATHS,
  PAYMENT_PATH,
  SHIPPING_PATH,
} from '../../frontend/react/src/constants/checkout';
import { OrderSummarySection } from './Components/OrderSummarySection/OrderSummarySection';
import { Payment } from './pages/Payment/Payment';
import { Shipping } from './pages/Shipping/Shipping';
import styles from './CheckoutPage.module.css';

interface Props {
  testid?: string;
}

export function Checkout(props: Props) {
  const { testid } = props;

  const history = useHistory();
  const location = useLocation();
  const isDesktop = useMedia(BreakpointQueryLarge);

  const containerRef = useRef<null | HTMLDivElement>(null);
  const scrollRef = useRef<null | HTMLDivElement>(null);

  // Add user and experiments to datadog
  useLogUser();
  useLogExperiments();

  useEffect(() => {
    if (
      !isDesktop &&
      scrollRef?.current &&
      history.location.pathname === PATHS[PAYMENT_PATH]
    ) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [history.location.pathname]);

  const { pageViewTrackingGA, eventTrackingGA } = useGoogleAnalytics();

  useEffect(() => {
    pageViewTrackingGA(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    eventTrackingGA({ category: 'transaction', action: 'start' });
  }, []);

  useEffect(() => {
    fbq('track', 'InitiateCheckout');
  }, []);

  useEffect(() => {
    if (window.DD_RUM === undefined) {
      return;
    }
    initO11y(window.DD_RUM);
  }, []);
  // Clear quiz local storage if it exists when an authenticated user lands on /checkout/*. Quiz local storage could exist here for users who signed up before onSignupSuccess included a quiz local storage reset and hadn't checked out at the time that the reset was deloyed. Local storage is cleared in hungryroot.tsx as another fallback.
  useClearQuizLocalStorage();

  const customer = useAuthState();
  const customerId = customer.customerData?.id;
  const orderId = customer.customerData?.cart;

  const { data: serverAddress } = useGetAddress();

  const [summaryCollapsed, setSummaryCollapsed] = useState(true);

  const [addressState, setAddressState] = useState<Address>();
  const [addressDetailsState, setAddressDetailsState] =
    useState<AddressDetails>();
  const [errorMessage, setErrorMessage] = useState<ErrorFields>();

  const persistCustomerAddress = usePatchCustomerAddress();
  const displayAddress = getDisplayAddress(addressState, serverAddress);
  const displayAddressDetails = getDisplayAddressDetails(
    addressDetailsState,
    serverAddress
  );

  const exp159 = useExperiment(159);
  const inExp159Variant = exp159 !== undefined && exp159 > 0;

  const budgetRangePlanVariant = useExperiment(177);
  const isInBudgetRangePlanVariant =
    budgetRangePlanVariant !== undefined && budgetRangePlanVariant > 0;

  const loc = useLocation();

  const isPaymentPath = loc.pathname === PATHS[PAYMENT_PATH];

  const exp164 = useExperiment(164);
  const showFlexCreditMessage =
    exp164 !== undefined && exp164 > 0 && !isPaymentPath;

  useEffect(() => {
    if (displayAddress === undefined) {
      return;
    }

    history.listen((newLocation, action) => {
      // Listen for browser navigation and redirect from payment page if required fields are incomplete
      if (
        action === 'REPLACE' &&
        newLocation.pathname === PATHS[SHIPPING_PATH]
      ) {
        const requiredFieldsError = validateRequiredFields({
          ...displayAddress,
          ...displayAddressDetails,
        });
        if (requiredFieldsError !== null) {
          setErrorMessage({ ...requiredFieldsError });
        }
      }
    });
  }, [location, displayAddress, displayAddressDetails]);

  const {
    data: deliveryDates,
    isLoading: isLoadingDeliveryDate,
    error: deliveryDateError,
    fetchStatus: fetchStatusDeliveryDate,
  } = useGetPossibleDays(
    {
      orderId: orderId as number,
      customerId: customerId,
      address: { ...displayAddress, ...displayAddressDetails },
      zipcode: displayAddress?.zipcode,
    },
    {
      enabled:
        serverAddress &&
        serverAddress.zip_code === displayAddress?.zipcode &&
        orderId
          ? true
          : false,
    }
  );
  const deliveryDateLoading =
    isLoadingDeliveryDate && fetchStatusDeliveryDate !== 'idle';

  const [selectedDate, setSelectedDate] = useState<undefined | DateInfo>(
    undefined
  );

  useEffect(() => {
    deliveryDates !== undefined && setSelectedDate(deliveryDates.selected_date);
  }, [deliveryDates]);

  const uesPostSaveShippingDayMutation = usePostSaveShippingDay();

  const [paymentLoading, setPaymentLoading] = useState(false);

  const { data: checkoutPricing, isLoading: isLoadingCheckoutPricing } =
    useCustomerCheckoutPricingInfo();

  const loading = <Loading />;

  if (isLoadingCheckoutPricing || !orderId || !checkoutPricing) {
    return loading;
  }

  if (exp159 === undefined) {
    return null;
  }

  const isPaymentRoute = matchPath(location.pathname, {
    path: PATHS[PAYMENT_PATH],
    exact: true,
    strict: false,
  });

  const firstBoxTotal = Number(checkoutPricing.first_box_pricing.total);
  const subtotal = Number(checkoutPricing.first_box_pricing.subtotal);
  const discount = Number(checkoutPricing.first_box_pricing.discount);
  const shipping = Number(checkoutPricing.first_box_pricing.shipping_cost);
  const potentialCredit = Number(
    checkoutPricing.first_box_pricing.potential_credit
  );
  const promoEffect = checkoutPricing.first_box_promo_effect;
  // we cannot use this promoEffectString directly because it shows
  // Offer Applied: free snack or sweet for life + free shipping. Spend $99 for 30% off your first delivery!
  // and we just want to show Spend $99 for 30% off your first delivery!
  const promoEffectString = promoEffect?.effect_str;
  const evergreenThreshold = checkoutPricing?.evergreen_threshold || 0;
  const freeShippingDelta = checkoutPricing?.free_shipping_delta;

  const promos = getPromos(promoEffect.promos, discount);

  const applicablePromo = getApplicablePromo(promos);
  const cyoPromo = getCyoPromo(promos);

  const promoText = getPromoForOverlay(
    subtotal,
    promoEffect.effect_base,
    applicablePromo,
    cyoPromo
  );

  const amountSummary = (headingVariant?: HeadingVariant) =>
    inExp159Variant ? (
      <OrderSummaryTotalAmountVariant
        testid="checkout-amount-summary"
        total={firstBoxTotal}
        headingVariant={headingVariant}
      />
    ) : (
      <OrderSummaryTotalAmount
        testid="checkout-amount-summary"
        total={firstBoxTotal}
        subtotal={
          getNumPromosWithDollarEffect(subtotal, promos) ? subtotal : undefined
        }
      />
    );
  const smsOptInDisclaimer = (
    <Paragraph variant="caption" className={styles.disclaimer}>
      *{showFlexCreditMessage && <>*</>}By checking this box, you agree to
      receive personalized deals and other messages via text from Hungryroot.
      Message & data rates may apply. Reply HELP for help and STOP to cancel.
      View{' '}
      <a href="/terms" target="_blank" className={styles['disclaimer-link']}>
        Terms
      </a>{' '}
      &{' '}
      <a href="/privacy" target="_blank" className={styles['disclaimer-link']}>
        Privacy
      </a>
    </Paragraph>
  );

  const continueButton = () => (
    <Button
      size="large"
      prominence="primary"
      style={inExp159Variant ? 'accent' : 'default'}
      textPrimary="Continue to payment"
      accentColor={inExp159Variant ? 'avocado' : undefined}
      onClick={() => {
        if (displayAddress === undefined) {
          return setErrorMessage({
            ...errorMessage,
            modal: {
              heading: ErrorMessage.genericHeading,
              body: ErrorMessage.genericBody,
            },
          });
        }

        const addressObj = {
          name:
            displayAddressDetails?.name ??
            `${customer.customerData?.first_name} ${customer.customerData?.last_name}`,
          ...displayAddress,
          ...displayAddressDetails,
        };

        const requiredFieldsError = validateRequiredFields(addressObj);
        if (requiredFieldsError !== null) {
          setErrorMessage({ ...requiredFieldsError });
        } else {
          setErrorMessage(undefined);
          persistCustomerAddress.mutate(
            {
              address: getAddressRequestBody(addressObj),
            },
            {
              onSuccess() {
                localStorage.removeItem(SODIUM_HARD_CONSTRAINT_OPT_OUT_LS_KEY);
                selectedDate !== undefined &&
                  orderId &&
                  uesPostSaveShippingDayMutation.mutate({
                    payload: {
                      order_id: orderId.toString(),
                      selected_date: selectedDate.value,
                    },
                    data: {
                      customerId,
                      address: {
                        ...displayAddress,
                        ...displayAddressDetails,
                      },
                    },
                  });

                if (selectedDate !== undefined) {
                  const data = {
                    customerId,
                    orderId,
                    address: {
                      ...displayAddress,
                      ...displayAddressDetails,
                    },
                  };
                  log('checkout_address_submit', data);

                  history.push(PATHS[PAYMENT_PATH]);
                }
              },
              onError(e) {
                const { heading, body } = getAddressErrorMessage(e);
                const data = {
                  customerId,
                  orderId,
                  address: {
                    ...displayAddress,
                    ...displayAddressDetails,
                  },
                  error: e,
                };
                log('checkout_address_submit_error', data);

                setErrorMessage({
                  ...errorMessage,
                  modal: {
                    heading,
                    body,
                  },
                });
              },
            }
          );
        }
      }}
      width="full-width"
      className={styles.continue}
    />
  );

  const checkoutButton = (buttonProps?: SubmitButtonProps) => (
    <Button
      size="large"
      prominence="primary"
      style={inExp159Variant ? 'accent' : 'default'}
      accentColor={inExp159Variant ? 'avocado' : undefined}
      textPrimary="Confirm order + see my cart"
      width="full-width"
      className={styles.continue}
      form="payment-submit"
      buttonType="submit"
      loading={paymentLoading}
      {...buttonProps}
    />
  );

  const checkoutOrContinueButton = isPaymentRoute
    ? checkoutButton()
    : continueButton();

  const orderSummary = (className?: string) =>
    inExp159Variant ? (
      <OrderSummary
        testid="pre-checkout-plan-order-summary"
        summaryText="Summary"
        orderId={orderId}
        containerClassName={styles['order-summary']}
        amountSummary={amountSummary()}
        subtotal={subtotal}
        shipping={shipping}
        promos={promos}
        hidePromo
        totalText="First order total"
        innerContainerClassName={className}
      ></OrderSummary>
    ) : (
      <OrderSummarySection
        subtotalLabel={isInBudgetRangePlanVariant ? 'Subtotal*' : 'Weekly plan'}
        freeShippingDelta={freeShippingDelta}
        showPricingNudge={
          subtotal < evergreenThreshold && freeShippingDelta === null
            ? Boolean(promoEffectString)
            : undefined
        }
        promos={promos}
        amountSummary={amountSummary()}
        subtotal={subtotal}
        shipping={shipping}
        orderId={orderId}
        promoText={promoText}
        continueButton={checkoutOrContinueButton}
        credit={potentialCredit}
        isCollapsed={summaryCollapsed}
        setIsCollapsed={setSummaryCollapsed}
        flexCreditMessaging={showFlexCreditMessage}
      />
    );

  const calloutBox = (
    <CalloutBox
      ref={scrollRef}
      className={styles.box}
      image={
        <div data-testid="open-box-svg" className={styles['open-box']}>
          <OpenBoxNew />
        </div>
      }
      callout={
        "You'll see your first cart with our recommendations after checkout. Edit and choose exactly what you want."
      }
    />
  );

  const orderSummaryFooterOverlay = (
    <OrderSummaryFooterOverlay
      summaryText="First order total:"
      amountSummary={amountSummary('H3')}
      continueButton={checkoutOrContinueButton}
      discount={discount}
      subtotal={subtotal}
      discountLabel={applicablePromo?.label}
    >
      {orderSummary(styles['footer-summary'])}
    </OrderSummaryFooterOverlay>
  );

  return (
    <CustomerContext.Provider value={customer}>
      <AuthGuard
        authenticated={(auth) => {
          const body = (
            <div data-testid={testid} className={styles.container}>
              {inExp159Variant ? (
                <Header />
              ) : (
                <nav className={styles.top}>
                  <div className={styles.nav}>
                    <img
                      className={styles.logo}
                      alt="Hungryroot logo"
                      src={LOGO_PATH}
                    />
                  </div>
                </nav>
              )}
              <div className={styles.inner} ref={containerRef}>
                <div
                  className={`${styles.checkout} ${
                    summaryCollapsed ? styles.collapsed : styles.expanded
                  }`}
                >
                  <OneColTwoCol
                    leftCol={
                      <>
                        <Heading
                          variant={inExp159Variant ? 'H1Alt' : 'H2'}
                          className={styles['checkout-heading']}
                        >
                          Checkout
                        </Heading>
                        {!isDesktop && !inExp159Variant && calloutBox}
                        <Switch>
                          <Route path={PATHS[SHIPPING_PATH]}>
                            <Shipping
                              testid={testid}
                              customer={auth.customerData}
                              addressState={addressState}
                              setAddressState={setAddressState}
                              addressDetailsState={addressDetailsState}
                              setAddressDetailsState={setAddressDetailsState}
                              errorMessage={errorMessage}
                              setErrorMessage={setErrorMessage}
                              displayAddress={displayAddress}
                              displayAddressDetails={displayAddressDetails}
                              orderId={orderId as number}
                              setSelectedDate={setSelectedDate}
                              selectedDate={selectedDate}
                              deliveryDates={deliveryDates}
                              deliveryDateError={
                                deliveryDateError?.message as string
                              }
                              deliveryDateLoading={deliveryDateLoading}
                              containerRef={containerRef}
                            />
                          </Route>
                          <Route path={PATHS[PAYMENT_PATH]}>
                            {displayAddress === undefined ||
                            validateRequiredFields({
                              ...displayAddress,
                              ...displayAddressDetails,
                            }) ||
                            !deliveryDates ? (
                              <Redirect to={PATHS[SHIPPING_PATH]} />
                            ) : (
                              <Payment
                                testid={testid}
                                zipcode={displayAddress.zipcode}
                                setPaymentLoading={setPaymentLoading}
                                paymentLoading={paymentLoading}
                                orderId={orderId as number}
                                subtotal={subtotal}
                                orderTotal={firstBoxTotal}
                                deliveryDate={deliveryDates.selected_date.label}
                                address={{
                                  ...displayAddress,
                                  ...displayAddressDetails,
                                }}
                                customerId={customerId}
                                plan={customer.customerData?.plan}
                                checkoutButton={
                                  isDesktop ? checkoutButton : undefined
                                }
                              />
                            )}
                          </Route>
                        </Switch>
                        {isDesktop &&
                          !isPaymentRoute &&
                          checkoutOrContinueButton}
                        <Switch>
                          <Route path={PATHS[SHIPPING_PATH]}>
                            {!isDesktop && showFlexCreditMessage && (
                              <FlexCreditCheckoutMessage />
                            )}
                            {smsOptInDisclaimer}
                          </Route>
                        </Switch>
                      </>
                    }
                    rightCol={
                      <div>
                        {isDesktop && orderSummary(styles['container-inner'])}
                        {isDesktop && !inExp159Variant && calloutBox}
                        {isDesktop && showFlexCreditMessage && (
                          <FlexCreditCheckoutMessage />
                        )}
                      </div>
                    }
                  />
                </div>
              </div>
              {!isDesktop && !inExp159Variant && orderSummary()}
              {!isDesktop && inExp159Variant && (
                <div className={styles['footer-overlay-visible']}>
                  {orderSummaryFooterOverlay}
                </div>
              )}
            </div>
          );

          return body;
        }}
      />
    </CustomerContext.Provider>
  );
}
